import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Crown, Trophy, Medal, Building2, Zap } from "lucide-react";
import { weekLabel } from "@/components/Badges";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeTick } from "@/lib/realtime";
import { Card } from "@/components/ui/card";
import { UserAvatar } from "@/components/UserAvatar";
import { ShareChampionButton } from "@/components/ChampionShare";
import { MONTH_NAMES, fmtNum, roleLabel } from "@/lib/helpers";

function Trophy3D({ c, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: i * 0.06 }}
                className="relative rounded-2xl border border-yellow-500/40 bg-gradient-to-b from-yellow-500/15 to-slate-900/60 p-4 text-center overflow-hidden"
                data-testid={`showcase-trophy-${c.year}-${c.month}`}>
      <div className="absolute -top-8 left-1/2 -translate-x-1/2 h-20 w-20 rounded-full bg-yellow-400/20 blur-2xl" />
      <div className="relative mx-auto h-14 w-14 rounded-2xl bg-yellow-400/20 border border-yellow-400/50 flex items-center justify-center mb-2">
        <Trophy size={28} className="text-yellow-300" />
      </div>
      <div className="font-display text-lg font-bold">{MONTH_NAMES[c.month - 1]}</div>
      <div className="text-xs font-mono text-slate-400">{c.year}</div>
      <div className="mt-2 font-display text-2xl font-black text-yellow-300" data-testid={`showcase-qty-${c.year}-${c.month}`}>{fmtNum(c.qty)}</div>
      <div className="text-[10px] uppercase font-mono text-slate-500">adet · {fmtNum(c.count)} işlem</div>
      <ShareChampionButton champ={c} label="Paylaş" className="relative mt-3 px-3 py-1 text-xs" />
    </motion.div>
  );
}

function WeekChip({ w, i }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }}
                className="flex items-center gap-3 rounded-xl border border-cyan-500/40 bg-gradient-to-r from-cyan-500/15 to-transparent px-3 py-2.5"
                data-testid={`showcase-week-${w.week_start}`}>
      <span className="h-9 w-9 rounded-lg bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center shrink-0"><Zap size={17} className="text-cyan-300" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{weekLabel(w)}</div>
        <div className="text-[10px] font-mono text-slate-500">{w.week_start.slice(0, 4)} · {fmtNum(w.count)} işlem</div>
      </div>
      <div className="font-display text-lg font-black text-cyan-300 tabular-nums" data-testid={`showcase-week-qty-${w.week_start}`}>{fmtNum(w.qty)}</div>
      <ShareChampionButton champ={w} label="" className="h-8 w-8 shrink-0" />
    </motion.div>
  );
}

function ProfileHeader({ p }) {
  const cur = p.current;
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass p-6 flex flex-wrap items-center gap-6 animate-fade-up" data-testid="profile-header">
      <div className="relative">
        <UserAvatar path={p.photo_path} name={p.full_name} className="h-24 w-24" rounded="rounded-3xl" />
        {p.month_leader_now && <span className="absolute -top-3 -right-3 h-9 w-9 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg shadow-yellow-500/40"><Crown size={18} className="text-slate-900" /></span>}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-display text-3xl font-bold" data-testid="profile-name">{p.full_name}</div>
        <div className="text-sm text-slate-400 flex items-center gap-2 mt-1"><Building2 size={14} /> {p.branch_name || "Şube yok"} · {roleLabel(p)}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {p.month_leader_now && <span className="inline-flex items-center gap-1 rounded-full border border-yellow-400/60 bg-yellow-400/15 text-yellow-200 text-xs px-2 py-0.5" data-testid="profile-month-lead"><Crown size={11} /> Ayın Lideri</span>}
          {p.week_leader_now && <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/60 bg-cyan-400/15 text-cyan-200 text-xs px-2 py-0.5" data-testid="profile-week-lead"><Zap size={11} /> Haftanın Lideri</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        {[["Aylık Kupa", p.champions.length, "profile-titles", "text-yellow-300"],
          ["Haftalık Kupa", (p.week_champions || []).length, "profile-week-titles", "text-cyan-300"],
          [`${MONTH_NAMES[cur.month - 1]} Adet`, fmtNum(cur.qty), "profile-month-qty", "text-blue-300"],
          ["Bu Ay Sıra", cur.rank ? `${cur.rank}/${cur.participants}` : "—", "profile-rank", "text-emerald-300"]].map(([l, v, t, c]) => (
          <div key={t} className="rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3">
            <div className="text-[10px] uppercase font-mono text-slate-500">{l}</div>
            <div className={`font-display text-2xl font-bold ${c}`} data-testid={t}>{v}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Profile() {
  const { uid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const id = uid || user?.id;
  const [p, setP] = useState(null);
  const [people, setPeople] = useState([]);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales"));
  useEffect(() => { http.get("/profiles").then((r) => setPeople(r.data)); }, []);
  useEffect(() => { if (id) http.get(`/profiles/${id}`).then((r) => setP(r.data)); }, [id, tick]);

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Award className="text-yellow-400" /> Rozet Vitrini</h1>
          <p className="text-slate-400 text-sm mt-1">Aylık satış şampiyonlukları, tarih ve adetleriyle</p>
        </div>
        <select value={id || ""} onChange={(e) => nav(`/profile/${e.target.value}`)} data-testid="profile-picker"
                className="h-10 rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm min-w-[240px]">
          {people.map((x) => <option key={x.id} value={x.id}>{x.full_name}{x.branch_name ? ` · ${x.branch_name}` : ""}{x.titles ? ` · ${x.titles} kupa` : ""}</option>)}
        </select>
      </header>
      {!p ? <div className="text-slate-400">Yükleniyor...</div> : (
        <>
          <ProfileHeader p={p} />
          <div>
            <div className="text-xs font-mono uppercase text-slate-400 mb-3 flex items-center gap-1.5"><Medal size={13} /> Kupa Dolabı</div>
            {p.champions.length ? (
              <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="showcase-grid">
                {p.champions.map((c, i) => <Trophy3D key={`${c.year}-${c.month}`} c={c} i={i} />)}
              </div>
            ) : (
              <Card className="border-dashed border-slate-700 bg-transparent p-10 text-center text-sm text-slate-500" data-testid="showcase-empty">
                Henüz aylık şampiyonluk yok. Ayın en çok satış adedine ulaşan personel ve en yüksek şube toplamına ulaşan müdür bu vitrine kupa ekler.
              </Card>
            )}
          </div>
          <div>
            <div className="text-xs font-mono uppercase text-slate-400 mb-3 flex items-center gap-1.5"><Zap size={13} /> Haftanın Şampiyonu</div>
            {(p.week_champions || []).length ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="showcase-weeks">
                {p.week_champions.map((w, i) => <WeekChip key={w.week_start} w={w} i={i} />)}
              </div>
            ) : (
              <Card className="border-dashed border-slate-700 bg-transparent p-6 text-center text-sm text-slate-500" data-testid="showcase-weeks-empty">
                Henüz haftalık şampiyonluk yok. Pazartesi–Pazar arası en çok satış adedine ulaşan personel ve en yüksek şube toplamına ulaşan müdür bu bölüme rozet ekler.
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
